
import { FloatingCard, CircleDecor } from "@/components/ui/visual";
import { motion } from "framer-motion";
import { Bot, Coins, Trophy, Target, Zap, Heart, Brain, Gift } from "lucide-react";

const DemoSection = () => {
  const features = [
    {
      icon: Bot,
      title: "Conversação Inteligente",
      description: "Terap-IA utiliza processamento de linguagem natural avançado para entender suas emoções e oferecer respostas empáticas e personalizadas."
    },
    {
      icon: Brain,
      title: "Análise Emocional",
      description: "Nossa IA detecta padrões emocionais em tempo real, identificando gatilhos e oferecendo insights valiosos sobre seu bem-estar mental."
    },
    {
      icon: Heart,
      title: "Suporte 24/7",
      description: "Disponível a qualquer momento, Terap-IA oferece apoio emocional imediato quando você mais precisa, sem julgamentos."
    }
  ];

  const gamificationFeatures = [
    {
      icon: Coins,
      title: "Ganhe Seshs",
      description: "Complete sessões de terapia e atividades de bem-estar para ganhar nossa moeda interna, os Seshs.",
      reward: "+10 Seshs por sessão completa"
    },
    {
      icon: Trophy,
      title: "Conquistas",
      description: "Desbloqueie marcos importantes em sua jornada de autoconhecimento e bem-estar mental.",
      reward: "Badges exclusivos"
    },
    {
      icon: Target,
      title: "Metas Personalizadas",
      description: "Estabeleça objetivos diários e semanais adaptados ao seu ritmo e necessidades.",
      reward: "Bônus de consistência"
    },
    {
      icon: Gift,
      title: "Recompensas Especiais",
      description: "Use seus Seshs para desbloquear conteúdo premium, meditações exclusivas e análises avançadas.",
      reward: "Conteúdo premium"
    }
  ];

  return (
    <section className="py-20 relative z-10 bg-gradient-to-br from-white via-gray-50/50 to-brand-primary/5">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <motion.div
              className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-2xl flex items-center justify-center shadow-lg"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <Bot className="w-8 h-8 text-white" />
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent">
              Terap-IA
            </h2>
          </div>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Nosso agente de inteligência artificial terapêutica, projetado para oferecer suporte emocional 
            personalizado e acompanhar sua jornada de bem-estar mental com tecnologia de ponta.
          </p>
        </motion.div>

        {/* Terap-IA Features */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.2 }}
            >
              <FloatingCard className="p-8 h-full text-center hover:shadow-xl transition-all duration-300">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  <feature.icon className="w-8 h-8 text-brand-primary" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </FloatingCard>
            </motion.div>
          ))}
        </div>

        {/* Conversation Demo */}
        <motion.div 
          className="max-w-4xl mx-auto mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <FloatingCard className="p-8 relative overflow-hidden">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Como o Terap-IA Funciona</h3>
              <p className="text-gray-600">Experimente uma conversa real com nossa IA terapêutica</p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div className="bg-gray-50 rounded-2xl p-4 flex-1">
                  <p className="text-gray-700 italic">
                    "Olá! Eu sou o Terap-IA. Estou aqui para ouvir, conversar e ajudar você a explorar seus pensamentos com clareza e tranquilidade. Como você está se sentindo hoje?"
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 justify-end">
                <div className="bg-brand-primary/10 rounded-2xl p-4 max-w-md">
                  <p className="text-gray-700">
                    "Tenho me sentido um pouco ansioso com o trabalho ultimamente..."
                  </p>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 bg-gray-400 rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div className="bg-gray-50 rounded-2xl p-4 flex-1">
                  <p className="text-gray-700 italic">
                    "Entendo que o trabalho tem causado ansiedade. Isso é mais comum do que você imagina. Vamos explorar juntos o que especificamente está te deixando ansioso. Você poderia me contar mais sobre essas situações?"
                  </p>
                </div>
              </div>
            </div>
            
            <CircleDecor 
              className="absolute -bottom-10 -right-10 opacity-5" 
              size="lg" 
              variant="accent" 
            />
          </FloatingCard>
        </motion.div>

        {/* Gamification Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <motion.div
              className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Zap className="w-8 h-8 text-white" />
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
              Sistema Seshs
            </h2>
          </div>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Nossa moeda interna que transforma sua jornada de bem-estar em uma experiência 
            envolvente e recompensadora. Cada passo em direção ao seu bem-estar vale a pena!
          </p>
        </motion.div>

        {/* Gamification Features */}
        <div className="grid md:grid-cols-2 gap-8">
          {gamificationFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.15 }}
            >
              <FloatingCard className="p-8 h-full hover:shadow-xl transition-all duration-300 group">
                <div className="flex items-start gap-6">
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                  >
                    <feature.icon className="w-8 h-8 text-orange-500" />
                  </motion.div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">{feature.description}</p>
                    
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-3 border border-yellow-200">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-semibold text-orange-700">{feature.reward}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </FloatingCard>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          <motion.button
            className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-lg flex items-center gap-3 mx-auto"
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Bot className="w-6 h-6" />
            <span>Começar Conversa com Terap-IA</span>
          </motion.button>
          
          <p className="text-gray-500 mt-4">
            ✨ Ganhe 10 Seshs na sua primeira sessão
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default DemoSection;
